import React, { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import * as R from 'ramda';
import { NoAvailableCard } from 'common';
import { useUserRolePermissions } from 'hooks';
import { useGetTerminalsQuery } from '../../../customerAuthorizationsEndpoints';
import LocationsGrid from './LocationsGrid';

const AddLocationsSection = ({
  customerIdName,
  existingTerminals,
  isOpen,
  onAddTerminals,
  setIsOpen,
}) => {
  const { currentAffiliate = {} } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;
  const [selectedTerminalIds, setSelectedTerminalIds] = useState([]);

  useEffect(() => {
    setSelectedTerminalIds([]);
  }, [customerIdName, existingTerminals]);

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
  };
  const { data: terminalsQuery = [], isFetching } = useGetTerminalsQuery(
    { params },
    { skip: R.or(R.not(isOpen), R.isNil(customerIdName)) }
  );

  const existingTerminalIds = useMemo(
    () => existingTerminals.map(R.prop('terminalId')),
    [existingTerminals]
  );
  const candidates = useMemo(
    () => terminalsQuery.filter(({ terminalId }) => !existingTerminalIds.includes(terminalId)),
    [existingTerminalIds, terminalsQuery]
  );

  const handleCancel = () => {
    setSelectedTerminalIds([]);
    setIsOpen(false);
  };

  const handleAdd = () => {
    const selectedTerminals = candidates.filter(({ terminalId }) =>
      selectedTerminalIds.includes(terminalId)
    );
    onAddTerminals(selectedTerminals);
    setSelectedTerminalIds([]);
    setIsOpen(false);
  };

  const selectionGridProps = {
    checkboxSelection: true,
    disableRowSelectionOnClick: false,
    loading: isFetching,
    onRowSelectionModelChange: model => setSelectedTerminalIds(model),
    rowSelectionModel: selectedTerminalIds,
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'common.white',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack spacing={2} sx={{ p: 1.25 }}>
        {!isFetching && R.isEmpty(candidates) ? (
          <NoAvailableCard
            suggestions="All available locations are already added to this authorization."
            type="locations"
          />
        ) : (
          <LocationsGrid
            darkHeader
            framed={false}
            gridHeight={280}
            gridProps={selectionGridProps}
            hideColumns={['terminalLoadingStatus']}
            panelTitle="Select Locations to Add"
            quickFilterAriaLabel="Filter Locations to Add"
            quickFilterPlaceholder="Search available..."
            quickFilterPlacement="header"
            showSelectionHeaderCheckbox
            terminals={candidates}
          />
        )}
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
          <Typography color="text.secondary" variant="body2">
            {R.isEmpty(selectedTerminalIds)
              ? 'Select locations above'
              : `${selectedTerminalIds.length} selected`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={R.isEmpty(selectedTerminalIds)}
              onClick={handleAdd}
              variant="contained"
            >
              {`Add${R.isEmpty(selectedTerminalIds) ? '' : ` (${selectedTerminalIds.length})`}`}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

AddLocationsSection.propTypes = {
  customerIdName: string,
  existingTerminals: arrayOf(
    shape({
      terminalId: oneOfType([number, string]),
      terminalIdName: oneOfType([number, string]),
      terminalLoadingStatus: bool,
      terminalShortName: string,
    })
  ),
  isOpen: bool,
  onAddTerminals: func.isRequired,
  setIsOpen: func.isRequired,
};

AddLocationsSection.defaultProps = {
  customerIdName: null,
  existingTerminals: [],
  isOpen: false,
};

export default AddLocationsSection;
